import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface DocHubTipTag {
  id: string;
  brand_id: string;
  name: string;
  color: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const useDocHubTipsTags = (brandId: string) => {
  const queryClient = useQueryClient();

  const {
    data: tags,
    isLoading,
    error
  } = useQuery({
    queryKey: ['doc-hub-tips-tags', brandId],
    queryFn: async (): Promise<DocHubTipTag[]> => {
      const { data, error } = await supabase
        .from('os_doc_hub_tips_tags')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!brandId,
  });

  const createTagMutation = useMutation({
    mutationFn: async (tagData: Omit<DocHubTipTag, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('os_doc_hub_tips_tags')
        .insert(tagData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tips-tags', brandId] });
      toast({
        title: "Success",
        description: "Tag created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating tag:', error);
      toast({
        title: "Error",
        description: "Failed to create tag",
        variant: "destructive",
      });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DocHubTipTag> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_doc_hub_tips_tags')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tips-tags', brandId] });
      toast({
        title: "Success",
        description: "Tag updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating tag:', error);
      toast({
        title: "Error",
        description: "Failed to update tag",
        variant: "destructive",
      });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      const { data: tips, error: fetchError } = await supabase
        .from('os_doc_hub_tips')
        .select('id, tags')
        .eq('brand_id', brandId);

      if (fetchError) throw fetchError;

      for (const tip of tips || []) {
        let currentTags: string[] = [];
        
        if (Array.isArray(tip.tags)) {
          currentTags = (tip.tags as any[]).map(String);
        } else if (typeof tip.tags === 'string') {
          try {
            const parsed = JSON.parse(tip.tags);
            currentTags = Array.isArray(parsed) ? parsed.map(String) : [];
          } catch {
            currentTags = [];
          }
        }

        if (currentTags.includes(tagId)) {
          const updatedTags = currentTags.filter(id => id !== tagId);
          
          const { error: updateError } = await supabase
            .from('os_doc_hub_tips')
            .update({ tags: JSON.stringify(updatedTags) })
            .eq('id', tip.id);

          if (updateError) throw updateError;
        }
      }

      const { error } = await supabase
        .from('os_doc_hub_tips_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tips-tags', brandId] });
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tips', brandId] });
      toast({
        title: "Success",
        description: "Tag deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      });
    },
  });

  const createTag = (tagData: Omit<DocHubTipTag, 'id' | 'created_at' | 'updated_at'>) => {
    createTagMutation.mutate(tagData);
  };

  const updateTag = (id: string, updates: Partial<DocHubTipTag>) => {
    updateTagMutation.mutate({ id, ...updates });
  };

  const deleteTag = (id: string) => {
    deleteTagMutation.mutate(id);
  };

  return {
    tags: tags || [],
    isLoading,
    error,
    createTag,
    updateTag,
    deleteTag,
    isCreating: createTagMutation.isPending,
    isUpdating: updateTagMutation.isPending,
    isDeleting: deleteTagMutation.isPending,
  };
};
